import apiService from "./api";
import { transformMealsForRestaurantCompanies } from "@/mappers/reportMapper";
import { courierLockAction, isHeldByCourier, releasesCourier, serverState } from "@/utils/guestOrderStatus";
import { tr } from "@/utils/i18nRef";

const COURIER_API = "https://api.kurir.ordera.app/api";

// orderId -> minuti pripreme izabrani pri prihvatanju (vidi rememberPrepMinutes)
const pendingPrepMinutes = new Map();

// orderId -> kad je zaštita zadnji put videla da je narudžba kod dostavljača. Ekrani posle "spremno" lokalno upišu novi
// status u narudžbu, pa bi sledeći klik izgledao kao da dostavljača nema; zato se to pamti, najviše 15 min ili do
// otkazivanja/odbijanja (kad dostavljač stvarno gubi narudžbu). Ponovno učitan izveštaj opet vrati pravo stanje.
const HELD_MEMORY_MS = 15 * 60 * 1000;
const heldMemory = new Map();
const heldByCourier = (id, order) => {
  if (isHeldByCourier(order)) {
    heldMemory.set(id, Date.now());
    return true;
  }
  const seen = heldMemory.get(id);
  if (seen && Date.now() - seen < HELD_MEMORY_MS) return true;
  heldMemory.delete(id);
  return false;
};

const reportService = {
  getFoodOrdersOrdered(restaurantId, params) {
    return apiService.get(
      `/reports/daily/food-orders-ordered/${restaurantId}`,
      params
    );
  },
  // Gotovi zbirovi po firmi, jelu, grupi i smeni; filter firmi (companies=934,960) radi backend
  getFoodOrdersSummary(restaurantId, params) {
    return apiService.get(
      `/reports/daily/food-orders-summary/${restaurantId}`,
      params
    );
  },
  getShiftOrderStatus(restaurantId, params) {
    return apiService.get(
      `/v2/reports/daily/restaurant/${restaurantId}/companies`,
      params
    );
  },
  getCompanyFoodKorisnici(companyId, params) {
    return apiService.get(
      `/reports/daily/company/${companyId}/food-korisnici`,
      params
    );
  },
  getCompanyMealKorisnici(companyId, params) {
    return apiService.get(
      `/reports/daily/company/${companyId}/korisnici`,
      params
    );
  },
  getCompanyFoodKorisniciByDay(companyId, params) {
    return apiService.get(
      `/reports/daily/company/${companyId}/food-korisnici-by-day`,
      params
    );
  },
  getRestaurantChildRestaurant(restaurantId, params) {
    return apiService.get(
      `/reports/daily/restaurant/${restaurantId}/child-restaurant`,
      params
    );
  },
  // Added methods for refactored components:
  getRestaurantCompanies(restaurantId, params) {
    return apiService.get(
      `/reports/daily/restaurant/${restaurantId}/companies`,
      params
    );
  },
  getTotalShiftMeals(restaurantId, params) {
    return apiService.get(`/reports/daily/total/${restaurantId}`, params);
  },
  getReservedMeals(restaurantId, params) {
    return apiService.get(
      `/reports/daily/restaurant/${restaurantId}/rezervni-obroci`,
      params
    );
  },
  getReservedMealsV2(restaurantId, params) {
    return apiService.get(
      `/v2/reports/daily/restaurant/${restaurantId}/reserved-meals`,
      params
    );
  },
  async getMealsForRestaurantCompanies(restaurantId, params) {
    const { data } = await apiService.get(
      `/reports/daily/restaurant/${restaurantId}/obrok`,
      params
    );
    const response = transformMealsForRestaurantCompanies(data);
    return response;
  },
  async getPackagesForRestaurantCompanies(restaurantId, params) {
    const { data } = await apiService.get(
      `/reports/daily/restaurant/${restaurantId}/obrok`,
      params
    );
    const { transformMealsForPackages } = await import("@/mappers/reportMapper");
    return transformMealsForPackages(data);
  },
  /** Odgovor /obrok bez pretvaranja: značke menija računaju iz njega obroke i pakovanje. */
  getMealsRaw(restaurantId, params) {
    return apiService.get(
      `/reports/daily/restaurant/${restaurantId}/obrok`,
      params
    );
  },
  getMealsForRestaurant(restaurantId, params) {
    return apiService.get(
      `/reports/daily/restaurant/${restaurantId}/obrok-report`,
      params
    );
  },
  getCompanyUserlist(params) {
    return apiService.post(`/reports/daily/company/userlists`, params);
  },
  getDailyRestaurant(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}`, params);
  },
  // config (neobavezno): npr. { timeout } za kuhinjski ekran, koji izvještaj pita svakih 10 s
  getNoContractDelivery(restaurantId, params, config) {
    return apiService.get(
      `/reports/daily/company/${restaurantId}/no-contract`,
      params,
      config
    );
  },
  getCompanyUserList(companyId, params) {
    return apiService.get(
      `reports/daily/company/${companyId}/userlist`,
      params
    );
  },
  // Add these methods for menu and additional meal
  getMenuForCurrentDate(payload) {
    return apiService.post("/menus/get-menu-for-current-date", payload);
  },
  addAdditionalMeal(payload) {
    return apiService.post("/orders/add-additional-meal", payload);
  },
  // New methods for employee order history
  getEmployeeOrdersByMonth(payload) {
    return apiService.post("/user/orders-by-month", payload);
  },
  getEmployeeOrdersByMonthCustom(url, payload) {
    return apiService.post(url, payload);
  },
  // Guest report methods
  getQrTable(restaurantId, params) {
    return apiService.get(`/v2/reports/daily/qr-table/${restaurantId}`, params);
  },
  getQrTableMultiple(params) {
    return apiService.get('/v2/reports/daily/qr-table-multiple', params);
  },
  getMultipleRestaurantGuestObrok(params) {
    return apiService.get('/reports/daily/multiple-restaurant-guest/obrok', params);
  },
  getRestaurantGuestObrok(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant-guest/${restaurantId}/obrok`, params);
  },
  getCompanyGuestKorisnici(companyId, params) {
    return apiService.get(`/reports/daily/company-guest/${companyId}/korisnici`, params);
  },
  getCompanyUserlistGuest(companyIds, params) {
    return apiService.get(`/reports/daily/company/${companyIds}/userlist-guest`, params);
  },
  getCompanyNoContract(restaurantId, params) {
    return apiService.get(`/reports/daily/company/${restaurantId}/no-contract`, params);
  },
  getCompanyMultipleNoContract(params, config) {
    return apiService.get('/reports/daily/company/multiple-no-contract', params, config);
  },
  getCompanyNoContractNextDay(restaurantId, params) {
    return apiService.get(`/reports/daily/company/${restaurantId}/no-contract-next-day`, params);
  },
  getMultipleRestaurantGuest(params) {
    return apiService.get('/reports/daily/multiple-restaurant-guest', params);
  },
  getMultipleRestaurantGuestCompanies(params) {
    return apiService.get('/reports/daily/multiple-restaurant-guest/companies', params);
  },
  getMultipleTotalGuest(params) {
    return apiService.get('/reports/daily/multiple-total-guest', params);
  },
  getRestaurantMultipleRezervniObroci(params) {
    return apiService.get('/reports/daily/restaurant/multiple-rezervni-obroci', params);
  },
  getRestaurantMultipleChildRestaurant(params) {
    return apiService.get('/reports/daily/restaurant/multiple-child-restaurant', params);
  },
  getRestaurantGuest(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant-guest/${restaurantId}`, params);
  },
  getRestaurantGuestCompanies(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant-guest/${restaurantId}/companies`, params);
  },
  getTotalGuest(restaurantId, params) {
    return apiService.get(`/reports/daily/total-guest/${restaurantId}`, params);
  },
  getRestaurantRezervniObroci(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/rezervni-obroci`, params);
  },
  getRestaurantChildRestaurants(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/child-restaurant`, params);
  },
  // More restaurants methods
  getTotalRestaurantOrders(params) {
    return apiService.get('/reports/daily/restaurant/total-restaurant-orders', params);
  },
  getMoreRestaurantsOrders(params) {
    return apiService.post('/reports/daily/restaurant/more-restaurants-orders', params);
  },
  getCompanyNewsMoreRestaurants(params) {
    return apiService.post('/reports/daily/company-news-more-restaurants', params);
  },
  getCompanyMoreRestaurantUserlists(params) {
    return apiService.post('/reports/daily/company-more-restaurant/userlists', params);
  },
  getMoreRestaurantsCompanyUserlist(companyIds, params) {
    return apiService.get(`/reports/daily/more-restaurants-company/${companyIds}/userlist`, params);
  },
  getRestaurantFirmsReports(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/firms-reports`, params);
  },
  // Izvestaj.vue methods
  getMoreRestaurantReports(restaurantId, params) {
    return apiService.get(`/reports/daily/more-restaurant/${restaurantId}/reports`, params);
  },
  async getMoreRestaurantReportsNew(restaurantId, params) {
    const { data } = await apiService.get(`/reports/daily/restaurant/${restaurantId}/order-more-restaurants`, params);
    const { transformMealsForRestaurantCompanies } = await import("@/mappers/reportMapper");
    const response = transformMealsForRestaurantCompanies(data);
    return response;
  },
  async getPackagesForMoreRestaurant(restaurantId, params) {
    const { data } = await apiService.get(`/reports/daily/restaurant/${restaurantId}/order-more-restaurants`, params);
    const { transformMealsForPackages } = await import("@/mappers/reportMapper");
    return transformMealsForPackages(data);
  },
  // Više restorana: "Jela" (paketi razloženi na jela, pomnoženi brojem paketa)
  async getMoreRestaurantsDishes(restaurantId, params) {
    const { data } = await apiService.get(`/reports/daily/restaurant/${restaurantId}/more-restaurants/dishes`, params);
    const { transformMealsForPackages } = await import("@/mappers/reportMapper");
    return transformMealsForPackages(data, { separateByCombination: true });
  },
  // Više restorana: "Pakovanje" (isti paket sa različitim izborima je poseban red, polje combination)
  async getMoreRestaurantsPacking(restaurantId, params) {
    const { data } = await apiService.get(`/reports/daily/restaurant/${restaurantId}/more-restaurants/packing`, params);
    const { transformMealsForPackages } = await import("@/mappers/reportMapper");
    return transformMealsForPackages(data, { separateByCombination: true });
  },
  // Više restorana: radnici po stavkama (items, total, show_amount)
  getCompanyMoreRestaurantsKorisniciItems(companyId, params) {
    return apiService.get(`/reports/daily/company-more-restaurants/${companyId}/korisnici-items`, params);
  },
  // Sve firme – zbirni izvještaj: orders + orders_more_restaurants, sabrano na backendu
  getAllCompaniesSummary(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/all-companies-summary`, params);
  },
  getAllCompaniesPacking(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/all-companies-summary/packing`, params);
  },
  getTotalMoreRestaurants(restaurantId, params) {
    return apiService.get(`/reports/daily/total-more-restaurants/${restaurantId}`, params);
  },
  getFoodOrdersOrdered(restaurantId, params) {
    return apiService.get(`/reports/daily/food-orders-ordered/${restaurantId}`, params);
  },
  getFoodOrdersOrderedV2(restaurantId, params) {
    return apiService.get(`/v2/reports/daily/food-orders-ordered/${restaurantId}`, params);
  },
  getMoreRestaurantFirmsReports(restaurantId, params) {
    return apiService.get(`/reports/daily/more-restaurant/${restaurantId}/firms-reports`, params);
  },
  getReservedMealsFirms(restaurantId) {
    return apiService.get(`/reports/daily/reserved-meals/${restaurantId}/firms`);
  },
  getRestaurantRezervniObroci(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/rezervni-obroci`, params);
  },
  getRestaurantObrok(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/obrok`, params);
  },
  getMoreRestaurantObrok(restaurantId, params) {
    return apiService.get(`/reports/daily/more-restaurant/${restaurantId}/obrok`, params);
  },
  getCompanyUserlistfood(companyIds, params) {
    return apiService.get(`/reports/daily/company/${companyIds}/userlistfood`, params);
  },
  getMoreRestaurantsCompanyUserlist(companyIds, params) {
    return apiService.get(`/reports/daily/more-restaurants-company/${companyIds}/userlist`, params);
  },
  getCompanyMoreRestaurantsKorisnici(companyId, params) {
    return apiService.get(`/reports/daily/company-more-restaurants/${companyId}/korisnici`, params);
  },
  getRestaurantCompanies(restaurantId, params) {
    return apiService.get(`/reports/daily/get-restaurant-companies/${restaurantId}`, params);
  },
  getRestaurantReportCompanies(restaurantId, params) {
    return apiService.get(`/reports/daily/restaurant/${restaurantId}/companies`, params);
  },
  getRestaurantTotal(restaurantId, params) {
    return apiService.get(`/reports/daily/total/${restaurantId}`, params);
  },
  getCompanyFoodKorisnici(companyIds, params) {
    return apiService.get(`/reports/daily/company/${companyIds}/food-korisnici`, params);
  },
  // Vreme spremnosti hrane (kurirski API, 0-120 min): samo upisuje procenu, ne menja status narudžbe niti dostavljača.
  setReadyTime(orderId, minutes) {
    const value = Math.min(120, Math.max(0, Math.round(Number(minutes) || 0)));
    return apiService.post(`${COURIER_API}/orders/${orderId}/ready`, { minutes: value });
  },
  // Minuti pripreme koje je restoran izabrao pri prihvatanju: čuvaju se dok prihvatanje ne prođe, pa idu kao vreme spremnosti.
  rememberPrepMinutes(orderId, minutes) {
    const value = Number(minutes);
    if (Number.isFinite(value) && value > 0) pendingPrepMinutes.set(Number(orderId), value);
  },
  // Change order status
  changeOrderStatus(data) {
    const id = Number(data.order_id ?? data.id);
    const target = serverState(data.status);
    // Mora pre slanja: `data.state` je već novi status, a stanje dostavljača je u data.order_guest_status.
    const lock = courierLockAction(heldByCourier(id, data), target);
    if (releasesCourier(target)) heldMemory.delete(id);
    if (lock === "block") {
      pendingPrepMinutes.delete(id);
      const message = tr(
        "courier_lock_blocked",
        "Dostavljač je već dodeljen ovoj narudžbi. Promena statusa bi ga sada skinula sa narudžbe, pa je privremeno isključena."
      );
      const error = new Error(message);
      error.response = { status: 409, data: { message } };
      return Promise.reject(error);
    }
    if (lock === "ready-time") {
      pendingPrepMinutes.delete(id);
      return reportService.setReadyTime(id, 0);
    }
    const minutes = target === "accepted" ? pendingPrepMinutes.get(id) : undefined;
    pendingPrepMinutes.delete(id);
    return apiService
      .post(`/reports/change-order-status`, {
        ...data,
        status: target,
        state: serverState(data.state),
      })
      .then((response) => {
        if (!minutes) return response;
        // prihvaćeno je prošlo: sada se upisuje izabrano vreme pripreme; neuspeh ne poništava prihvatanje
        return reportService
          .setReadyTime(id, minutes)
          .catch((error) => console.warn("Ready time was not saved:", error))
          .then(() => response);
      });
  },
  // Change order status via courier service
  changeCourierOrderStatus(orderId, status) {
    return apiService.post(
      "https://api.kurir.ordera.app/api/orders/change-status",
      { order_id: orderId, status }
    );
  },
  // Export report to Excel
  exportToExcel(data, config = {}) {
    return apiService.post(`/reports/export-excel`, data, {
      responseType: 'blob',
      ...config
    });
  },
  // Get company locations
  getCompanyLocations(data) {
    return apiService.post(`/reports/daily/company/locations`, data);
  },
  // Get company userlists
  getCompanyUserlists(data) {
    return apiService.post(`/reports/daily/company/userlists`, data);
  },
  // Fetch credit report. Expect params to include any filters (date, date2, company_ids, etc.).
  getCreditUser(params = {}) {
    return apiService.get(`/credits/report`, params);
  },
};

export default reportService;
